<template>

  <v-container fluid class="pt-8 pb-8" v-if="order">
    <v-row class="align-center">

      <!------------------------------>
      <!-- Delivery Status Workflow -->
      <!------------------------------>
      <v-col cols="12">
        <div class="title mb-8">Tracking History</div>

        <!------------------>
        <!-- Order Placed -->
        <!------------------>
        <div>
          <div class="text-subtitle-1 font-weight-black">
            <v-icon class="mr-2">mdi-checkbox-blank-circle</v-icon>
            Order placed
          </div>
          <div class="border-left">
            <div class="ml-6 pt-3">
              <span class="attr-label pr-3">submitted:</span>
              <span>{{ formatDateAndTime(orderPlacedDate) }}</span>
            </div>
            <div class="ml-6 mt-3 pb-4">
              <span v-if="submitterName">
                <span class="attr-label pr-3">submitted by:</span>
                <span>{{ submitterName }} ({{ order.shipper.name }})</span>
              </span>
              <span v-else>
                <span class="attr-label pr-3">submitted by:</span>
                <span>{{ order.shipper.name }}</span>
              </span>
            </div>
          </div>
        </div>

        <!------------------------------------------------------->
        <!-- Order Scheduled, Delivery History, and Canceled -->
        <!------------------------------------------------------->
        <div v-for="(attempt, index) in orderHistory" :key="`delivery-attempt-${index}`">
          <!----------------------------------------->
          <!-- Order Archived (workflow ends here) -->
          <!-- NOTE: This is a special status for  -->
          <!-- pre-Beta customers.                 -->
          <!----------------------------------------->
          <div v-if="attempt.order_status === 'ARCHIVED'">
            <div class="text-subtitle-1 font-weight-black">
              <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'archived') }}</v-icon>
              Order archived (viewable by shipper only)
            </div>
            <div class="border-left-no-line" v-if="attempt.completed_at">
              <div class="ml-6 pt-3">
                <span class="attr-label pr-3">archived:</span>
                <span>{{ formatDateAndTime(attempt.completed_at) }}</span>
              </div>
            </div>
          </div>

          <!----------------------------------------->
          <!-- Order Canceled (workflow ends here) -->
          <!----------------------------------------->
          <div v-else-if="attempt.order_status === 'CANCELED'">
            <div class="text-subtitle-1 font-weight-black">
              <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'canceled') }}</v-icon>
              Order canceled
            </div>
            <div class="border-left-no-line" v-if="attempt.canceled_at">
              <div class="ml-6 pt-3">
                <span class="attr-label pr-3">canceled:</span>
                <span>{{ formatDateAndTime(attempt.canceled_at) }}</span>
              </div>
              <div class="ml-6 mt-3">
                <span class="attr-label pr-3">canceled by:</span>
                <span>{{ order.shipper.name }}</span>
              </div>
            </div>
          </div>

          <!---------------------------------------->
          <!-- Order Scheduled & Delivery History -->
          <!---------------------------------------->
          <template v-else>
            <div>
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'scheduled') }}</v-icon>
                Order scheduled for delivery
              </div>
              <div class="border-left">
                <div class="ml-6 pt-3 pb-4" v-if="attempt.scheduled_at && attempt.order_status !== 'BEING_SCHEDULED'">
                  <span class="attr-label pr-3">scheduled:</span>
                  <span>{{ formatDateAndTime(attempt.scheduled_at) }}</span>
                </div>
                <div class="ml-6 pb-4" v-if="attempt.order_status === 'SCHEDULED' && attempt.estimated_shipper_arrival_time">
                  <span class="attr-label pr-3">est. pickup:</span>
                  <span>{{ formatDateAndTime(attempt.estimated_shipper_arrival_time) }}</span>
                </div>
                <div class="ml-6 pb-4" v-if="!attempt.rescheduled_at && !attempt.rejected_at && !attempt.completed_at && attempt.order_status !== 'BEING_SCHEDULED' && attempt.estimated_receiver_arrival_time">
                  <span class="attr-label pr-3">est. delivery:</span>
                  <span>{{ formatDateAndTime(attempt.estimated_receiver_arrival_time) }}</span>
                </div>
              </div>
            </div>

            <!-- Order Rescheduled (before pickup) -->
            <div v-if="attempt.order_status === 'RESCHEDULED' && !attempt.picked_up_at">
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'rescheduled') }}</v-icon>
                Order being rescheduled
              </div>
              <div class="border-left" v-if="attempt.rescheduled_at">
                <div class="ml-6 pt-3 pb-4">
                  <span class="attr-label pr-3">rescheduling at:</span>
                  <span>{{ formatDateAndTime(attempt.rescheduled_at) }}</span>
                </div>
                <div class="ml-6 pb-4">
                  <span class="attr-label pr-3">initiated by:</span>
                  <span>{{ order.shipper.name }}</span>
                </div>
                <div class="ml-6 pb-4" v-if="attempt.dispatcher_notes && attempt.dispatcher_notes.reschedule">
                  <span class="attr-label pr-3">notes:</span>
                  <span>{{ attempt.dispatcher_notes.reschedule }}</span>
                </div>
              </div>
            </div>

            <template v-else>
              <!-- Order Picked Up -->
              <div>
                <div class="text-subtitle-1 font-weight-black">
                  <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'picked-up') }}</v-icon>
                  Order picked-up
                </div>
                <div class="border-left">
                  <div class="ml-6 pt-3 pb-4" v-if="attempt.picked_up_at">
                    <span class="attr-label pr-3">picked-up:</span>
                    <span>{{ formatDateAndTime(attempt.picked_up_at) }}</span>
                  </div>
                </div>
              </div>

              <!-- Order Rescheduled (after pickup) -->
              <div v-if="attempt.order_status === 'RESCHEDULED'">
                <div class="text-subtitle-1 font-weight-black">
                  <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'rescheduled') }}</v-icon>
                  Order being rescheduled
                </div>
                <div class="border-left" v-if="attempt.rescheduled_at">
                  <div class="ml-6 pt-3 pb-4">
                    <span class="attr-label pr-3">rescheduling at:</span>
                    <span>{{ formatDateAndTime(attempt.rescheduled_at) }}</span>
                  </div>
                  <div class="ml-6 pb-4">
                    <span class="attr-label pr-3">initiated by:</span>
                    <span>{{ order.shipper.name }}</span>
                  </div>
                  <div class="ml-6 pb-4" v-if="attempt.dispatcher_notes && attempt.dispatcher_notes.reschedule">
                    <span class="attr-label pr-3">notes:</span>
                    <span>{{ attempt.dispatcher_notes.reschedule }}</span>
                  </div>
                </div>
              </div>

              <!-- Order Rejected -->
              <div v-else-if="attempt.rejected_at">
                <div class="text-subtitle-1 font-weight-black">
                  <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'rejected') }}</v-icon>
                  Order rejected on delivery
                </div>
                <div class="border-left">
                  <div class="ml-6 pt-3">
                    <span class="attr-label pr-3">rejected:</span>
                    <span>{{ formatDateAndTime(attempt.rejected_at) }}</span>
                  </div>
                  <div class="ml-6 mt-3" v-if="attempt.receiving_agent_name">
                    <span class="attr-label pr-3">receiving agent:</span>
                    <span>{{ formatAgentName(attempt.receiving_agent_name) }}</span>
                  </div>
                  <div class="ml-6 mt-3 pb-4" v-if="attempt.receiver_notes">
                    <span class="attr-label pr-3">notes:</span>
                    <span>{{ attempt.receiver_notes }}</span>
                  </div>
                </div>
              </div>

              <!-- Order Delivered -->
              <div v-else>
                <div class="text-subtitle-1 font-weight-black">
                  <v-icon class="mr-2">{{ getWorkflowIcon(attempt, 'delivered') }}</v-icon>
                  Order delivered
                </div>
                <div class="border-left-no-line" v-if="attempt.delivered_at">
                  <div class="ml-6 pt-3">
                    <span class="attr-label pr-3">delivered:</span>
                    <span>{{ formatDateAndTime(attempt.delivered_at) }}</span>
                  </div>
                  <div class="ml-6 mt-3" v-if="attempt.receiving_agent_name">
                    <span class="attr-label pr-3">receiving agent:</span>
                    <span>{{ formatAgentName(attempt.receiving_agent_name) }}</span>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </div>
      </v-col>

    </v-row>
  </v-container>

</template>

<script>
export default {
  name: 'OrderTrackingHistory',

  props: {
    order: {
      required: true
    },
    isCredit: {
      type: Boolean,
      default: false
    },
    shortenNames: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isDataLoading: false,
      activeError: null
    }
  },

  computed: {
    // -------------------- Order history (delivery attempts)
    orderHistory () {
      return (this.order.order_history && this.order.order_history.length > 0)
        ? this.order.order_history
        : [this.order]
    },
    orderPlacedDate () {
      return (this.orderHistory.length > 0)
        ? this.orderHistory[0].placed_at
        : this.order.placed_at
    },
    historicalDeliveryAttempts () {
      return (this.orderHistory.length > 1)
        ? this.orderHistory.slice(0, -1)
        : null
    },
    // -------------------- Submitter info
    submitter () {
      return this.order.submitter || {}
    },
    submitterName () {
      const firstName = this.submitter.first_name
      const lastName = this.submitter.last_name
      return (lastName) ? `${firstName} ${lastName}` : firstName
    }
  },

  methods: {
    formatDateAndTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMMM Do, YYYY - h:mm a z') : ''
    },

    formatAgentName (name = '') {
      if (this.shortenNames) {
        const words = name.replace(/\s+/g, ' ').trim().split(' ')
        return (words.length > 1) ? `${words[0]} ${words[1][0]}.` : words[0]
      } else {
        return name
      }
    },

    getWorkflowIcon (order = {}, step) {
      switch (step) {
        case 'canceled': {
          return 'mdi-checkbox-blank-circle'
        }
        case 'scheduled': {
          return (order.estimated_shipper_arrival_time && order.order_status !== 'BEING_SCHEDULED')
            ? 'mdi-checkbox-blank-circle'
            : 'mdi-checkbox-blank-circle-outline'
        }
        case 'rescheduled': {
          return 'mdi-checkbox-blank-circle'
        }
        case 'picked-up': {
          return (order.picked_up_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'rejected': {
          return (order.rejected_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'delivered': {
          return (order.delivered_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'archived': {
          return 'mdi-checkbox-blank-circle'
        }
        default: return 'mdi-checkbox-blank-circle-outline'
      }
    }
  }
}
</script>

<style scoped>
  .attr-label {
    color: #9e9e9e;
  }

  .border-left {
    font-size: 14px;
    border-left: 2px solid white;
    margin-left: 11px;
    margin-top: 5px;
    margin-bottom: 5px;
    min-height: 50px;
    align-content: center;
  }

  .border-left-no-line {
    font-size: 14px;
    margin-left: 13px;
    margin-top: 5px;
    margin-bottom: 5px;
    min-height: 50px;
    align-content: center;
  }

  .center {
    padding-top: 3%;
  }
</style>
